import jsQR from 'jsqr'
import { useRef, useEffect, useCallback } from 'react'
import style from '../style/components/qrcode-scanner.module.scss'
import { ManualGetOrder } from './manual-get-order'

interface QRCodeScannerProps {
  className?: string
  setQRcode: (url: string) => void
}

const QRCodeScanner = ({ className, setQRcode }: QRCodeScannerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamsRef = useRef<MediaStream | null>(null)

  const stopStream = useCallback(() => {
    if (streamsRef.current) {
      const tracks = streamsRef.current?.getTracks()
      tracks?.forEach((track) => {
        track.stop()
      })
      streamsRef.current = null
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject = null
      }
    }
  }, [])

  useEffect(() => {
    if (!videoRef.current || !canvasRef.current) {
      return
    }
    const video = videoRef.current
    const canvasElement = canvasRef.current
    const canvas = canvasElement.getContext('2d')
    if (!canvas) return

    const onStreaming = () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvasElement.height = video.videoHeight
        canvasElement.width = video.videoWidth
        canvas.drawImage(video, 0, 0, canvasElement.width, canvasElement.height)
        const imageData = canvas.getImageData(
          0,
          0,
          canvasElement.width,
          canvasElement.height,
        )
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        })

        if (code?.data) {
          stopStream()
          setQRcode(code.data)
          video.pause()
        }
      }
      // Stop requestAnimationFrame if the stream is stopped
      if (streamsRef.current) {
        window.requestAnimationFrame(onStreaming)
      }
    }

    video.onplay = () => {
      window.requestAnimationFrame(onStreaming)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const activeWebcam = async () => {
      if (streamsRef.current) return
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      streamsRef.current = stream
      // Play the video
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        // required to tell iOS safari we don't want fullscreen
        videoRef.current.play()
      }
    }

    activeWebcam()

    return () => {
      stopStream()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <video ref={videoRef} className={style.video} playsInline />
      <canvas id="canvas" ref={canvasRef} className={className} />
      <ManualGetOrder />
    </>
  )
}

export default QRCodeScanner
